import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FileText, Calendar, BarChart3, Filter, RefreshCw, Download, ArrowLeft, CheckCircle2, XCircle, Clock, AlertCircle, Loader2, Edit, ToggleLeft, ToggleRight, History, Copy } from 'lucide-react';
import { reportsService } from '../services/reportsService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import RevenueTrendChart from '../components/revenue/RevenueTrendChart';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * Report Preview Page
 * Displays report configuration and generated data
 */
const ReportPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  // Fetch report
  useEffect(() => {
    if (id) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await reportsService.get(parseInt(id));
      setReport(response.data);
      
      // If report is ready, try to fetch generated data
      // Note: The backend doesn't store generated data, so we'll need to generate it
      if (response.data?.status === 'ready') {
        await generateReportData();
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load report';
      setError(errorMessage);
      console.error('Report fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateReportData = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      // For now, we'll need to call the generation service
      // Since there's no direct endpoint, we'll update the report status
      // and the backend will generate it when accessed
      // This is a placeholder - in a real implementation, you'd call a generate endpoint
      
      // Update report to trigger generation
      await reportsService.update(parseInt(id), {
        status: 'generating'
      });
      
      // Wait a bit and refresh
      setTimeout(() => {
        fetchReport();
      }, 2000);
      
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to generate report';
      setError(errorMessage);
      console.error('Report generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (format) => {
    try {
      let exportFn;
      switch (format) {
        case 'csv':
          exportFn = reportsService.exportCsv;
          break;
        case 'json':
          exportFn = reportsService.exportJson;
          break;
        case 'pdf':
          exportFn = reportsService.exportPdf;
          break;
        default:
          return;
      }

      await exportFn(parseInt(id));
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to export report';
      alert(errorMessage);
      console.error('Export error:', err);
    }
  };

  const handleScheduleToggle = async () => {
    try {
      const updatedSchedule = {
        ...report.schedule,
        enabled: !report.schedule?.enabled,
      };

      await reportsService.update(parseInt(id), {
        schedule: updatedSchedule,
      });

      // Refresh report
      await fetchReport();
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to update schedule';
      alert(errorMessage);
      console.error('Schedule update error:', err);
    }
  };

  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      const response = await reportsService.duplicate(parseInt(id));
      
      // Navigate to the new duplicate
      if (response.data?.report_id) {
        navigate(`/reports/${response.data.report_id}`);
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to duplicate report';
      alert(errorMessage);
      console.error('Duplicate error:', err);
    } finally {
      setDuplicating(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', icon: FileText, label: 'Draft' },
      ready: { variant: 'default', icon: CheckCircle2, label: 'Ready' },
      generating: { variant: 'secondary', icon: Clock, label: 'Generating' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Failed' },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(value);
    }
    return value;
  };

  // Format number
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
      return value.toLocaleString();
    }
    return value;
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return `${value.toFixed(2)}%`;
    }
    return value;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="mt-2 sm:mt-3 lg:mt-4">
          <Button onClick={() => navigate('/reports')} variant="outline" size="sm" className="text-xs">
            <ArrowLeft className="w-3 h-3 mr-1.5" />
            Back to Reports
          </Button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Report not found</AlertDescription>
        </Alert>
        <div className="mt-2 sm:mt-3 lg:mt-4">
          <Button onClick={() => navigate('/reports')} variant="outline" size="sm" className="text-xs">
            <ArrowLeft className="w-3 h-3 mr-1.5" />
            Back to Reports
          </Button>
        </div>
      </div>
    );
  }

  const formatLabels = {
    table: 'Table',
    chart: 'Chart',
    both: 'Table & Chart',
  };

  const metricLabels = {
    revenue: 'Revenue',
    impressions: 'Impressions',
    clicks: 'Clicks',
    ecpm: 'eCPM',
    ctr: 'CTR',
    fill_rate: 'Fill Rate',
    completion_rate: 'Completion Rate',
    active_users: 'Active Users',
    sessions: 'Sessions',
  };

  const breakdownLabels = {
    app: 'App',
    ad_unit: 'Ad Unit',
    country: 'Country',
    ad_source: 'Ad Source',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/reports')} variant="outline" size="sm" className="text-xs">
              <ArrowLeft className="w-3 h-3 mr-1.5" />
              Back
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold break-words">{report.report_name}</h1>
              <p className="text-muted-foreground mt-0.5 text-xs">
                Created {new Date(report.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {getStatusBadge(report.status)}
            {(report.status === 'draft' || report.status === 'failed') && (
              <Button onClick={generateReportData} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            )}
            {report.status === 'ready' && (
              <>
                <Button onClick={fetchReport} variant="outline" disabled={generating}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport('csv')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('json')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export as JSON
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('pdf')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export as PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            <Button
              onClick={handleDuplicate}
              variant="outline"
              disabled={duplicating || generating}
            >
              {duplicating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Duplicating...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <Alert variant="destructive" className="mb-2 sm:mb-3 lg:mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Report Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="h-4 w-4" />
              Date Range
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Start Date</div>
              <div className="font-medium">
                {new Date(report.start_date).toLocaleDateString()}
              </div>
              <div className="text-sm text-muted-foreground mt-3">End Date</div>
              <div className="font-medium">
                {new Date(report.end_date).toLocaleDateString()}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <BarChart3 className="h-4 w-4" />
              Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {report.metrics?.map(metric => (
                <Badge key={metric} variant="outline">
                  {metricLabels[metric] || metric}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Filter className="h-4 w-4" />
              Breakdowns
            </CardTitle>
          </CardHeader>
          <CardContent>
            {report.breakdowns?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {report.breakdowns.map(breakdown => (
                  <Badge key={breakdown} variant="outline">
                    {breakdownLabels[breakdown] || breakdown}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No breakdowns</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Report Format */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Report Format</CardTitle>
        </CardHeader>
        <CardContent>
          <Badge variant="outline" className="text-base px-3 py-1">
            {formatLabels[report.format] || report.format}
          </Badge>
        </CardContent>
      </Card>

      {/* Schedule Configuration */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Clock className="h-4 w-4" />
              Schedule
            </CardTitle>
            {report.schedule && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleScheduleToggle}
              >
                {report.schedule.enabled ? (
                  <>
                    <ToggleRight className="w-4 h-4 mr-2" />
                    Disable
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-2" />
                    Enable
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {report.schedule?.enabled ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="default">
                  <Clock className="w-3 h-3 mr-1" />
                  Enabled
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Frequency</div>
                  <div className="font-medium capitalize">{report.schedule.frequency || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Time</div>
                  <div className="font-medium">{report.schedule.time || '-'}</div>
                </div>
              </div>
              {report.last_generated_at && (
                <div className="pt-2 border-t">
                  <div className="text-sm text-muted-foreground">Last Generated</div>
                  <div className="font-medium">
                    {new Date(report.last_generated_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-3">No schedule configured</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/reports/${id}/edit`)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Configure Schedule
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report History */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Report History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4 pb-4 border-b">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">Report Created</span>
                  <Badge variant="outline">Initial</Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  {new Date(report.created_at).toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Report was created and configured
                </div>
              </div>
            </div>

            {report.last_generated_at && (
              <div className="flex items-start gap-4 pb-4 border-b">
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">Last Generated</span>
                    <Badge variant="default">
                      {report.status === 'ready' ? 'Success' : report.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(report.last_generated_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Report data was last generated
                    {report.schedule?.enabled && ' (scheduled)'}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  report.status === 'ready' ? 'bg-green-500' :
                  report.status === 'generating' ? 'bg-yellow-500' :
                  report.status === 'failed' ? 'bg-destructive' :
                  'bg-muted'
                }`}></div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">Current Status</span>
                  {getStatusBadge(report.status)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {report.status === 'ready' && 'Report is ready and can be exported'}
                  {report.status === 'generating' && 'Report is being generated'}
                  {report.status === 'failed' && 'Report generation failed'}
                  {report.status === 'draft' && 'Report is in draft status and needs to be generated'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Data */}
      {report.status === 'generating' && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Generating report data...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {report.status === 'failed' && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <XCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
              <p className="text-lg font-medium mb-2">Report generation failed</p>
              <p className="text-muted-foreground mb-4">
                There was an error generating this report. Please try again.
              </p>
              <Button onClick={generateReportData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry Generation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {report.status === 'ready' && (
        <Card>
          <CardHeader>
            <CardTitle>Report Data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <p>Report data will be displayed here once generation is implemented.</p>
              <p className="text-sm mt-2">
                The backend generates report data on-demand. Data visualization will be added in a future update.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {report.status === 'draft' && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">Report not generated</p>
              <p className="text-muted-foreground mb-4">
                This report is in draft status. Generate it to view the data.
              </p>
              <Button onClick={generateReportData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportPreview;
